import { lazy, Suspense } from "react"
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useParams,
} from "react-router-dom"
import { Toaster as Sonner } from "@/components/ui/sonner"
import { Toaster } from "@/components/ui/toaster"
import { TooltipProvider } from "@/components/ui/tooltip"
import ProtectedRoute from "./auth/ProtectedRoute"
import { MagicLinkVerification } from "./components/auth/MagicLinkVerification"
import { EnvSwitcher } from "./components/EnvSwitcher"
import PageLoader from "./components/PageLoader"
import LoginPage from "./pages/LoginPage"
import NotFound from "./pages/NotFound"
import PasswordRecoveryPage from "./pages/PasswordRecoveryPage"
import ResetPasswordPage from "./pages/ResetPasswordPage"
import StaffLoginPage from "./pages/StaffLoginPage"
import TwoFactorPage from "./pages/TwoFactorPage"
import { lazyWithRetry } from "./utils/lazyWithRetry"

import { UsersIndexRoute } from "./pages/admin/UsersIndexRoute"
import { RequireScope } from "./components/auth/RequireScope"
import {
  BEEHIIV_READ,
  CAMPAIGN_READ,
  COMMUNITY_READ,
  EMAIL_READ,
  NOTIFICATIONS_SEND,
  EXERCISES_READ,
  FLAGS_READ,
  INSIGHTS_READ,
  LESSONS_READ,
  MEDIA_READ,
  OPS_JOBS_READ,
  OPS_READ,
  OPS_WRITE,
  PAYMENTS_READ,
  PAYMENTS_SUBSCRIPTIONS_READ,
  PORTAL_READ,
  PRICING_READ,
  PROMO_READ,
  REVIEWS_READ,
  SEGMENTS_READ,
  SIM_WRITE,
  USERS_PII_READ,
  USERS_READ,
  USERS_STATIC_CODE_READ,
  WAITLIST_READ,
} from "./auth/scopes"

const ProgrammeReviewPage = lazy(() => import("./pages/admin/ProgrammeReviewPage"))

const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"))
const UsersPage = lazyWithRetry(() => import("./pages/admin/UsersPage"))
const UsersLayout = lazyWithRetry(() => import("./pages/admin/UsersLayout"))
const UsersOverviewPage = lazyWithRetry(() => import("./pages/admin/UsersOverviewPage"))
const UserPage = lazy(() => import("./pages/admin/UserPage"))
const StoreReviewPage = lazy(() => import("./pages/admin/StoreReviewPage"))
const DbMigrationApprovalPage = lazy(() => import("./pages/admin/DbMigrationApprovalPage"))
const OpsLayout = lazy(() => import("./pages/admin/OpsLayout"))
const OpsBackfillsPage = lazy(() => import("./pages/admin/OpsBackfillsPage"))
const OpsMigrationsPage = lazy(() => import("./pages/admin/OpsMigrationsPage"))
const OpsJobsPage = lazy(() => import("./pages/admin/OpsJobsPage"))
const OpsJobPage = lazy(() => import("./pages/admin/OpsJobPage"))
const OpsBackfillPage = lazy(() => import("./pages/admin/OpsBackfillPage"))
const CommunityPage = lazy(() => import("./pages/admin/CommunityPage"))
const CommunityStatsPage = lazy(() => import("./pages/admin/CommunityStatsPage"))
const DailyLessonsPage = lazy(() => import("./pages/admin/DailyLessonsPage"))
const DailyLessonsBulkEditorPage = lazy(
  () => import("./pages/admin/DailyLessonsBulkEditorPage"),
)
const BannersPage = lazy(() => import("./pages/admin/BannersPage"))
const SimulationPage = lazy(() => import("./pages/admin/SimulationPage"))
const MediaLibraryPage = lazy(() => import("./pages/admin/MediaLibraryPage"))
const AppStoreScreenshotsPage = lazy(() => import("./pages/admin/AppStoreScreenshotsPage"))
const PromoCodesPage = lazy(() => import("./pages/admin/PromoCodesPage"))
const FeatureFlagsPage = lazy(() => import("./pages/admin/FeatureFlagsPage"))
const FeatureFlagEditorPage = lazy(() => import("./pages/admin/FeatureFlagEditorPage"))
const FlagChangeApprovalPage = lazy(() => import("./pages/admin/FlagChangeApprovalPage"))
const PricingPage = lazy(() => import("./pages/admin/PricingPage"))
const HardshipOfferPage = lazy(() => import("./pages/admin/HardshipOfferPage"))
const PaymentsOverviewPage = lazy(
  () => import("./pages/admin/payments/PaymentsOverviewPage"),
)
const PaymentsMetricsPage = lazy(() =>
  import("./pages/admin/payments/PaymentsOverviewPage").then((m) => ({
    default: m.PaymentsMetricsPage,
  })),
)
const PaymentsChartsPage = lazy(
  () => import("./pages/admin/payments/PaymentsChartsPage"),
)
const PaymentsSubscriptionsPage = lazy(
  () => import("./pages/admin/payments/PaymentsSubscriptionsPage"),
)
const PaymentsHealthPage = lazy(
  () => import("./pages/admin/payments/PaymentsHealthPage"),
)
const PaymentsAnomaliesPage = lazy(
  () => import("./pages/admin/payments/PaymentsAnomaliesPage"),
)
const ShipHealthPage = lazy(
  () => import("./pages/admin/payments/ShipHealthPage"),
)
const VitalSignsPage = lazy(() => import("./pages/admin/VitalSignsPage"))
const CampaignsPage = lazy(
  () => import("./pages/admin/payments/CampaignsPage"),
)
const CoachingBuyersPage = lazy(
  () => import("./pages/admin/payments/CoachingBuyersPage"),
)
const ReviewsPage = lazy(() => import("./pages/admin/ReviewsPage"))
const EmailOverviewPage = lazy(() => import("./pages/admin/EmailOverviewPage"))
const MailPersonPage = lazy(() => import("./pages/admin/MailPersonPage"))
const FindPersonRedirect = lazy(() =>
  import("./pages/admin/MailPersonPage").then((m) => ({ default: m.FindPersonRedirect })),
)
const EmailStatsPage = lazy(() => import("./pages/admin/EmailStatsPage"))
const CampaignPage = lazy(() => import("./pages/admin/CampaignPage"))
const BroadcastPage = lazy(() => import("./pages/admin/BroadcastPage"))
const EmailLayoutsPage = lazy(() => import("./pages/admin/EmailLayoutsPage"))
const TemplateStudioPage = lazy(
  () => import("./pages/admin/TemplateStudioPage"),
)
const EmailTemplatesPage = lazy(
  () => import("./pages/admin/EmailTemplatesPage"),
)
const UserSegmentsPage = lazy(() => import("./pages/admin/UserSegmentsPage"))
const EmailCampaignsPage = lazy(
  () => import("./pages/admin/EmailCampaignsPage"),
)
const PushListPage = lazy(() => import("./pages/admin/PushListPage"))
const EmailSendsPage = lazy(() => import("./pages/admin/EmailSendsPage"))
const EmailExecutionsPage = lazy(
  () => import("./pages/admin/EmailExecutionsPage"),
)
const InsightsPage = lazy(() => import("./pages/admin/InsightsPage"))
const JourneyPage = lazy(() => import("./pages/admin/JourneyPage"))
const ActivationOverviewPage = lazy(() => import("./pages/admin/ActivationOverviewPage"))
const CheckpointsPage = lazy(
  () => import("./pages/admin/CheckpointsPage"),
)
const RevenueCatImportPage = lazy(
  () => import("./pages/admin/RevenueCatImportPage"),
)
const BeehiivPage = lazy(() => import("./pages/admin/BeehiivPage"))
const NewsletterPage = lazy(() => import("./pages/admin/NewsletterPage"))
const TherapistWaitlistPage = lazy(
  () => import("./pages/admin/TherapistWaitlistPage"),
)
const FunnelLeadsPage = lazy(() => import("./pages/admin/FunnelLeadsPage"))
const PeoplePage = lazy(() => import("./pages/admin/PeoplePage"))

const ExerciseAnalyticsView = lazy(() =>
  import("./components/admin/ExerciseAnalyticsView").then((module) => ({
    default: module.ExerciseAnalyticsView,
  })),
)
const AmisPage = lazy(() => import("./pages/admin/amis/AmisPage"))
const AmisEditPage = lazy(() => import("./pages/admin/amis/AmisEditPage"))

/** /admin/customers/:id -> /admin/users/:id, keeping the id. */
function CustomerRedirect() {
  const { id } = useParams()
  return <Navigate to={`/admin/users/${id ?? ""}`} replace />
}

const App = () => {
  return (
    <TooltipProvider>
      <Toaster />
      <Sonner />
      {import.meta.env.DEV && <EnvSwitcher />}
      {/* v7_startTransition: navigation runs as a transition, so the page on
          screen stays put while the next lazy page loads instead of blanking. */}
      <Router future={{ v7_startTransition: true }}>
        <Routes>
          {/* Root redirects to admin */}
          <Route path="/" element={<Navigate to="/admin" replace />} />

          {/* Admin Portal - Protected Route with nested routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminLayout />
                </Suspense>
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/admin/dashboard" replace />} />
            {/* J1: the old dashboard folded into Payments Overview. The route
                stays so bookmarks resolve; it now redirects there. */}
            <Route
              path="dashboard"
              element={<Navigate to="/admin/payments" replace />}
            />
            <Route
              path="vital-signs"
              element={
                <RequireScope scopes={[INSIGHTS_READ]}>
                  <VitalSignsPage />
                </RequireScope>
              }
            />
            <Route
              path="insights"
              element={
                <RequireScope scopes={[INSIGHTS_READ]}>
                  <InsightsPage />
                </RequireScope>
              }
            />
            <Route path="programme" element={<RequireScope scopes={[LESSONS_READ]}><ProgrammeReviewPage /></RequireScope>} />
            <Route path="journey" element={<RequireScope scopes={[INSIGHTS_READ]}><JourneyPage /></RequireScope>} />
            <Route
              path="activation"
              element={
                <RequireScope scopes={[INSIGHTS_READ]}>
                  <ActivationOverviewPage />
                </RequireScope>
              }
            />
            <Route
              path="checkpoints"
              element={
                <RequireScope scopes={[INSIGHTS_READ]}>
                  <CheckpointsPage />
                </RequireScope>
              }
            />
            {/* The page was "Key user moments" until 2026-10; keep old bookmarks working. */}
            <Route path="key-user-moments" element={<Navigate to="/admin/checkpoints" replace />} />
            {/*
              Users: two tabs, each its own URL. The Overview (index) is
              aggregates under metrics:read; People (list) is the table under
              users:read. "list" is a static segment, so it always outranks
              users/:id below.
            */}
            <Route
              path="users"
              element={
                  <UsersLayout />
              }
            >
              <Route
                index
                element={
                  <UsersIndexRoute>
                      <UsersOverviewPage />
                  </UsersIndexRoute>
                }
              />
              <Route
                path="list"
                element={
                  <RequireScope scopes={[USERS_READ]}>
                      <UsersPage />
                  </RequireScope>
                }
              />
            </Route>
            {/*
              One person's page. Its header reads the payments-scoped customer
              view, so it needs both scopes; every role that holds one holds the
              other, so in practice it is the same gate as the list.
            */}
            <Route
              path="users/:id"
              element={
                <RequireScope scopes={[USERS_READ, PAYMENTS_SUBSCRIPTIONS_READ]}>
                    <UserPage />
                </RequireScope>
              }
            />
            {/* Customers merged into Users; keep old links and bookmarks working.
                /customers was for finding a person, so it lands on People. */}
            <Route path="customers" element={<Navigate to="/admin/users/list" replace />} />
            <Route path="customers/:id" element={<CustomerRedirect />} />
            <Route
              path="db-migrations/:id"
              element={
                <RequireScope scopes={[OPS_READ]}>
                    <DbMigrationApprovalPage />
                </RequireScope>
              }
            />
            <Route
              path="ops"
              element={
                <RequireScope scopes={[OPS_JOBS_READ]}>
                    <OpsLayout />
                </RequireScope>
              }
            >
              <Route index element={<OpsBackfillsPage />} />
              <Route path="backfills/:name" element={<OpsBackfillPage />} />
              <Route path="migrations" element={<OpsMigrationsPage />} />
              <Route path="jobs" element={<OpsJobsPage />} />
              <Route path="jobs/:jobId" element={<OpsJobPage />} />
            </Route>
            <Route
              path="store-review"
              element={
                <RequireScope scopes={[USERS_STATIC_CODE_READ]}>
                    <StoreReviewPage />
                </RequireScope>
              }
            />
            <Route
              path="community"
              element={
                <RequireScope scopes={[COMMUNITY_READ]}>
              <CommunityPage />
                </RequireScope>
              }
            />
            <Route
              path="community/stats"
              element={
                <RequireScope scopes={[COMMUNITY_READ]}>
                  <CommunityStatsPage />
                </RequireScope>
              }
            />
            <Route
              path="daily-lessons"
              element={
                <RequireScope scopes={[LESSONS_READ]}>
              <DailyLessonsPage />
                </RequireScope>
              }
            />
            <Route
              path="daily-lessons/bulk-editor"
              element={
                <RequireScope scopes={[LESSONS_READ]}>
              <DailyLessonsBulkEditorPage />
                </RequireScope>
              }
            />
            <Route
              path="banners"
              element={
                <RequireScope scopes={[PORTAL_READ]}>
              <BannersPage />
                </RequireScope>
              }
            />
            {import.meta.env.DEV && (
              <Route
              path="simulation"
              element={
                <RequireScope scopes={[SIM_WRITE]}>
              <SimulationPage />
                </RequireScope>
              }
            />
            )}
            <Route
              path="media"
              element={
                <RequireScope scopes={[MEDIA_READ]}>
              <MediaLibraryPage />
                </RequireScope>
              }
            />
            <Route
              path="app-store-screenshots"
              element={
                <RequireScope scopes={[MEDIA_READ]}>
                  <AppStoreScreenshotsPage />
                </RequireScope>
              }
            />
            <Route
              path="promo-codes"
              element={
                <RequireScope scopes={[PROMO_READ]}>
              <PromoCodesPage />
                </RequireScope>
              }
            />
            <Route
              path="feature-flags"
              element={
                <RequireScope scopes={[FLAGS_READ]}>
              <FeatureFlagsPage />
                </RequireScope>
              }
            />
            <Route
              path="feature-flags/:flagId"
              element={
                <RequireScope scopes={[FLAGS_READ]}>
                  <FeatureFlagEditorPage />
                </RequireScope>
              }
            />
            <Route
              path="flag-changes/:changeId"
              element={
                <RequireScope scopes={[FLAGS_READ]}>
                  <FlagChangeApprovalPage />
                </RequireScope>
              }
            />
            <Route
              path="stripe-pricing"
              element={
                <RequireScope scopes={[PRICING_READ]}>
              <PricingPage />
                </RequireScope>
              }
            />
            <Route
              path="hardship-offer"
              element={
                <RequireScope scopes={[PRICING_READ, USERS_PII_READ]}>
              <HardshipOfferPage />
                </RequireScope>
              }
            />
            <Route
              path="payments"
              element={
                <RequireScope scopes={[PAYMENTS_READ]}>
              <PaymentsOverviewPage />
                </RequireScope>
              }
            />
            <Route
              path="payments/metrics"
              element={
                <RequireScope scopes={[PAYMENTS_READ]}>
              <PaymentsMetricsPage />
                </RequireScope>
              }
            />
            <Route
              path="payments/charts"
              element={
                <RequireScope scopes={[PAYMENTS_READ]}>
              <PaymentsChartsPage />
                </RequireScope>
              }
            />
            <Route
              path="payments/ship-health"
              element={
                <RequireScope scopes={[PAYMENTS_READ]}>
              <ShipHealthPage />
                </RequireScope>
              }
            />
            <Route
              path="payments/subscriptions"
              element={
                <RequireScope scopes={[PAYMENTS_SUBSCRIPTIONS_READ]}>
              <PaymentsSubscriptionsPage />
                </RequireScope>
              }
            />
            <Route
              path="payments/campaigns"
              element={
                <RequireScope scopes={[PAYMENTS_READ]}>
              <CampaignsPage />
                </RequireScope>
              }
            />
            <Route
              path="payments/coaching"
              element={
                <RequireScope scopes={[PAYMENTS_SUBSCRIPTIONS_READ]}>
                  <CoachingBuyersPage />
                </RequireScope>
              }
            />
            <Route
              path="payments/health"
              element={
                <RequireScope scopes={[PAYMENTS_READ]}>
              <PaymentsHealthPage />
                </RequireScope>
              }
            />
            <Route
              path="payments/anomalies"
              element={
                <RequireScope scopes={[PAYMENTS_SUBSCRIPTIONS_READ]}>
              <PaymentsAnomaliesPage />
                </RequireScope>
              }
            />
            <Route
              path="reviews"
              element={
                <RequireScope scopes={[REVIEWS_READ]}>
              <ReviewsPage />
                </RequireScope>
              }
            />
            <Route
              path="therapist-waitlist"
              element={
                <RequireScope scopes={[WAITLIST_READ]}>
              <TherapistWaitlistPage />
                </RequireScope>
              }
            />
            <Route
              path="funnel-leads"
              element={
                <RequireScope scopes={[USERS_READ]}>
              <FunnelLeadsPage />
                </RequireScope>
              }
            />
            {/* Consents: everyone we hold an address for; addresses only with PII on. */}
            <Route
              path="consents"
              element={
                <RequireScope scopes={[EMAIL_READ, USERS_PII_READ]}>
                  <PeoplePage />
                </RequireScope>
              }
            />
            <Route
              path="consents/list"
              element={
                <RequireScope scopes={[EMAIL_READ, USERS_PII_READ]}>
                  <PeoplePage />
                </RequireScope>
              }
            />

            <Route
              path="email"
              element={
                <RequireScope scopes={[EMAIL_READ]}>
              <EmailOverviewPage />
                </RequireScope>
              }
            />
            <Route
              path="email/people/find"
              element={
                <RequireScope scopes={[EMAIL_READ, USERS_PII_READ]}>
                  <FindPersonRedirect />
                </RequireScope>
              }
            />
            <Route
              path="email/people/:kind/:id"
              element={
                <RequireScope scopes={[EMAIL_READ, USERS_PII_READ]}>
                  <MailPersonPage />
                </RequireScope>
              }
            />
            <Route
              path="email-stats"
              element={
                <RequireScope scopes={[EMAIL_READ]}>
                  <EmailStatsPage />
                </RequireScope>
              }
            />
            <Route
              path="email-templates"
              element={
                <RequireScope scopes={[EMAIL_READ]}>
              <EmailTemplatesPage />
                </RequireScope>
              }
            />
            <Route
              path="email-layouts"
              element={
                <RequireScope scopes={[EMAIL_READ]}>
                  <EmailLayoutsPage />
                </RequireScope>
              }
            />
            <Route
              path="email-templates/:templateId"
              element={
                <RequireScope scopes={[EMAIL_READ]}>
                  <TemplateStudioPage />
                </RequireScope>
              }
            />
            <Route
              path="user-segments"
              element={
                <RequireScope scopes={[SEGMENTS_READ]}>
              <UserSegmentsPage />
                </RequireScope>
              }
            />
            {["email-campaigns", "email-campaigns/how-they-are-doing", "email-campaigns/in-flight"].map((path) => (
              <Route
                key={path}
                path={path}
                element={
                  <RequireScope scopes={[CAMPAIGN_READ]}>
                    <EmailCampaignsPage />
                  </RequireScope>
                }
              />
            ))}
            <Route
              path="email-campaigns/:campaignId"
              element={
                <RequireScope scopes={[CAMPAIGN_READ]}>
                  <CampaignPage />
                </RequireScope>
              }
            />
            <Route
              path="push-list"
              element={
                <RequireScope scopes={[NOTIFICATIONS_SEND]}>
              <PushListPage />
                </RequireScope>
              }
            />
            <Route
              path="email-sends/:campaignId"
              element={
                <RequireScope scopes={[EMAIL_READ]}>
                  <BroadcastPage kind="one-off" />
                </RequireScope>
              }
            />
            <Route
              path="newsletter/editions/:campaignId"
              element={
                <RequireScope scopes={[EMAIL_READ]}>
                  <BroadcastPage kind="newsletter" />
                </RequireScope>
              }
            />

            <Route
              path="email-executions"
              element={
                <RequireScope scopes={[EMAIL_READ, USERS_PII_READ]}>
              <EmailExecutionsPage />
                </RequireScope>
              }
            />
            {/* The old Logs page was Executions filtered to problems. */}
            <Route
              path="email-logs"
              element={<Navigate to="/admin/email-executions?has_error=true" replace />}
            />
            <Route
              path="revenuecat/import"
              element={
                <RequireScope scopes={[OPS_WRITE]}>
              <RevenueCatImportPage />
                </RequireScope>
              }
            />
            <Route
              path="newsletter/:templateId"
              element={
                <RequireScope scopes={[EMAIL_READ]}>
                  <TemplateStudioPage />
                </RequireScope>
              }
            />
            {/* Send: the wizard under Broadcast. */}
            <Route
              path="newsletter/send"
              element={
                <RequireScope scopes={[EMAIL_READ]}>
                  <EmailSendsPage />
                </RequireScope>
              }
            />
            <Route
              path="newsletter"
              element={
                <RequireScope scopes={[EMAIL_READ]}>
                  <NewsletterPage />
                </RequireScope>
              }
            />
            <Route
              path="beehiiv"
              element={
                <RequireScope scopes={[BEEHIIV_READ]}>
                      <BeehiivPage />
                </RequireScope>
              }
            />
            <Route
              path="analytics/exercises"
              element={
                <RequireScope scopes={[EXERCISES_READ]}>
              <ExerciseAnalyticsView />
                </RequireScope>
              }
            />
            <Route
              path="analytics/exercises/:exerciseId"
              element={
                <RequireScope scopes={[EXERCISES_READ]}>
              <ExerciseAnalyticsView />
                </RequireScope>
              }
            />
            <Route
              path="amis"
              element={
                <RequireScope scopes={[EXERCISES_READ]}>
              <AmisPage />
                </RequireScope>
              }
            />
            <Route
              path="amis/new"
              element={
                <RequireScope scopes={[EXERCISES_READ]}>
              <AmisEditPage />
                </RequireScope>
              }
            />
            <Route
              path="amis/:id"
              element={
                <RequireScope scopes={[EXERCISES_READ]}>
              <AmisEditPage />
                </RequireScope>
              }
            />
          </Route>

          {/* Auth Routes - Admin Staff Only */}
          <Route
            path="/auth/login"
            element={
              <ProtectedRoute requireAuth={false}>
                <LoginPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/auth/staff-login"
            element={
              <ProtectedRoute requireAuth={false}>
                <StaffLoginPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/auth/password-recovery"
            element={
              <ProtectedRoute requireAuth={false}>
                <PasswordRecoveryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/auth/reset-password"
            element={
              <ProtectedRoute requireAuth={false}>
                <ResetPasswordPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/auth/verify"
            element={
              <ProtectedRoute requireAuth={false}>
                <MagicLinkVerification />
              </ProtectedRoute>
            }
          />
          {/* 2FA step: NOT wrapped in ProtectedRoute — that guard redirects any
              requiresTwoFactor session here, so wrapping it would loop. The page
              guards itself (→ /admin once verified, → staff-login if no challenge). */}
          <Route path="/auth/2fa" element={<TwoFactorPage />} />

          {/* 404 Catch-all */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Router>
    </TooltipProvider>
  )
}

export default App
